<template>
  <AccountCard :title="$t('pages.account.settings.sections.email.title')">
    <div class="my-3">
      <p>{{ props.profileDetails.email }}</p>
    </div>
    <hr class="my-6" />
    <div class="flex flex-row flex-wrap items-center gap-4">
      <VForm
        :validation-schema="Schema"
        :initial-values="Schema.getDefault()"
        v-slot="{ handleSubmit }"
        as="div"
        class="flex-1 min-w-[15rem]"
      >
        <form @submit="handleSubmit($event, mutate)" class="max-w-lg">
          <h4 class="font-semibold">{{ $t("pages.account.settings.sections.email.subtitle") }}</h4>
          <CommonAlert v-if="error" type="error" :content="error.message" class="mb-6 mt-4" />
          <div class="w-full mt-2">
            <div class="flex flex-row flex-wrap gap-x-2 md:gap-y-6 md:pt-4">
              <BaseVTextInput type="email" :label="$t('form.new_email')" name="email" class="flex-1 basis-full" />
              <BaseVTextInput
                type="email"
                :label="$t('form.new_email_confirmation')"
                name="email_confirmation"
                class="flex-1 basis-full"
              />
              <BaseVTextInput
                type="password"
                :label="$t('pages.account.settings.sections.password.title')"
                name="password"
                class="flex-1 basis-full"
              />
            </div>

            <div class="flex flex-row flex-wrap gap-4 items-center mt-8">
              <BaseButton
                type="submit"
                class="w-auto max-w-full whitespace-nowrap text-[1rem]"
                :loading="pending"
                size="medium"
              >
                {{ $t("pages.account.buttons.change_email") }}
              </BaseButton>

              <div class="flex-1 basis-1/3 text-primary shrink-0">
                <div class="flex flex-row gap-2 items-center" v-if="success">
                  <CheckCircleIcon class="w-[1.5em] h-[1.5em] shrink-0" />
                  <span>{{ $t("pages.account.texts.changes_saved") }}</span>
                </div>
              </div>
            </div>
          </div>
        </form>
      </VForm>

      <div class="bg-[#D8D8D84D] flex-grow md:flex-grow-0 basis-60 p-4 rounded-md text-sm">
        {{ $t("pages.account.settings.sections.email.confirmation_note") }}
      </div>
    </div>
  </AccountCard>
</template>

<script setup lang="ts">
import { useProfileStore } from "@/stores/profile";
import { CheckCircleIcon } from "@heroicons/vue/solid";
import { AxiosError } from "axios";
import * as yup from "yup";
import type { User } from "~/entities/User";
import AccountCard from "../components/account-card.vue";

interface AccountSettingsPersonalDetailsProps {
  profileDetails: User.ProfileDetails;
}
const props = defineProps<AccountSettingsPersonalDetailsProps>();

const Schema = yup.object({
  type: yup.string().optional().default("email"),
  email: yup.string().email().required(),
  email_confirmation: yup
    .string()
    .oneOf([yup.ref("email")])
    .required(),
  password: yup.string().required(),
});
type Schema = yup.InferType<typeof Schema>;

const error = ref<Error>();
const { mutate, pending, success } = useMutation({
  mutationFn: async (credentials: Schema) =>
    useAxios()
      .put("/profile/update", credentials)
      .then((res) => res.data),
  onError(err) {
    error.value = err;
    if (err instanceof AxiosError) error.value = new Error(err.response?.data.message);
  },
  async onSuccess() {
    error.value = undefined;
    // Fetch the updated profile details to update store
    const profileStore = useProfileStore();
    await profileStore.fetchProfile();
  },
});
</script>
